import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { useUsersQuery } from '../hooks/useUsersQuery';
import type { UserStatus } from '../types/users.types';
import { SearchBar, Select, Pagination, ButtonLink, QueryErrorAlert } from '@/shared/components';
import { UserDataTable } from '../components/UserDataTable';
import { DealerSelect } from '@/features/dealers/components/DealerSelect';
import type { Dealer } from '@/features/dealers/types/dealers.types';

interface SortConfig {
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

interface PaginationConfig {
  page: number;
  per_page: number;
}

export function UserList() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserStatus | undefined>(undefined);
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({});
  const [pagination, setPagination] = useState<PaginationConfig>({
    page: 1,
    per_page: 20,
  });

  const query = useUsersQuery({
    search: search || undefined,
    status,
    dealer_id: selectedDealer?.id,
    page: pagination.page,
    per_page: pagination.per_page,
    sort_by: sortConfig.sort_by,
    sort_direction: sortConfig.sort_direction,
  });

  const users = query.data?.users ?? [];
  const meta = query.data?.meta;

  const handleSort = (column: string) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSortConfig((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        sort_by: column,
        sort_direction: 'asc',
      };
    });
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePerPageChange = (per_page: number) => {
    setPagination({ page: 1, per_page });
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Users</h1>
        <PermissionGuard permission={{ resource: 'user', action: 'add' }}>
          <ButtonLink to="/admin/users/new" size="sm">Add User</ButtonLink>
        </PermissionGuard>
      </div>

      <div className="flex gap-2 flex-wrap">
        <SearchBar
          searchTerm={search}
          setSearchTerm={(term) => {
            setSearch(term);
            setPagination({ page: 1, per_page: pagination.per_page });
          }}
          placeholder="Search Users"
        />
        <Select
          options={[
            { value: '', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
          placeholder="Filter by status"
          value={status}
          onChange={(value) => {
            setStatus(value === '' ? undefined : (value as UserStatus));
            setPagination({ page: 1, per_page: pagination.per_page });
          }}
        />
        <PermissionGuard permission={{ resource: 'dealer', action: 'browse' }}>
          <div className="w-72">
            <DealerSelect
              selected={selectedDealer}
              onSelect={(dealer) => {
                setSelectedDealer(dealer);
                setPagination({ page: 1, per_page: pagination.per_page });
              }}
              placeholder="Filter by dealer..."
            />
          </div>
        </PermissionGuard>
      </div>

      <div className="space-y-4">
        {query.isError ? (
          <QueryErrorAlert message="Could not load users. You may not have access to this list, or the request failed." onRetry={() => query.refetch()} isRetrying={query.isFetching} />
        ) : (
          <>
            <UserDataTable
              data={users}
              isLoading={query.isLoading}
              sortConfig={sortConfig}
              onSort={handleSort}
              emptyMessage="No users found. Try adjusting your search criteria or create a new user."
            />

            {meta && meta.last_page > 1 && (
              <Pagination
                currentPage={meta.current_page}
                lastPage={meta.last_page}
                total={meta.total}
                perPage={meta.per_page}
                from={meta.from}
                to={meta.to}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}
